import React, { useState, useEffect, useContext } from "react";
import { Client, Hub, KeyValue } from "../../common/models";
import orgDataService from "services/orgDataService";
import adminService from "services/adminService";
import { Select, MenuItem, FormControl, InputLabel, Box, Paper, Grid, Checkbox, FormControlLabel, Divider, Typography, Chip } from "@mui/material";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers";
import MDButton from "components/MDButton";
import { Role } from "common/Constants";
import { AuthContext } from "common/AuthProvider";
import MultiSelect from "common/components/MultiSelect";

// The picker fires onChange on every keystroke; a half-typed year (0002) used to be searched (500s)
// and is now also saved, so only act once the date is real.
const isCompleteDate = (v: unknown) => !!v && moment(v as any).isValid() && moment(v as any).year() >= 2000;

const ListFilter = ({ search, setSearch, gridRef, clearFilters, totalCount = 0, stageCounts = {} as any, approveButton = null }) => {
  const { isUserInRole } = useContext(AuthContext);
  let isDispatcher: boolean = isUserInRole(Role.Dispatcher);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<KeyValue[]>([]);
  const [dispatchQueues, setDispatchQueues] = useState<any[]>([]);
  const [types, setTypes] = useState([
    { id: 1, name: "Barge", value: "barge" },
    { id: 2, name: "RailRoad", value: "standard" },
  ]);
  useEffect(() => {
    orgDataService.getAllHubs().then((data) => setHubs(data));
    orgDataService.getAllClients().then((data) => setClients(data));
    orgDataService.getOrderStatuses().then((data) => setOrderStatuses(data));
    adminService.getDispatchQueues(true).then((data) => setDispatchQueues(data));
  }, []);

  function handleOnChange(e: any) {
    let value = e.target.value;

    // Convert dispatchQueueConfigId to number or null
    if (e.target.name === "dispatchQueueConfigId") {
      value = value === "" ? null : Number(value);
    }

    var request = { ...search, [e.target.name]: value };
    setSearch(request);
    gridRef.current.setPage(0);
  }

  const selectSx = { p: 0 };
  const selectDisplayProps = { style: { padding: "0.75rem !important" as any } };
  const stageValue = (val: any) => val === null || val === undefined ? "all" : val.toString();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0", borderBottom: "none", borderRadius: "4px 4px 0 0", p: 1.5 }}>
      {/* Row 1: Core filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        {!isDispatcher && (
          <>
            <MultiSelect label={"Type"} values={types} selectedValues={search.tripTypes} setSelectedValues={(values) => setSearch({...search, tripTypes: values})} />
            <MultiSelect label={"Hub"} values={hubs} selectedValues={search.hubIds} setSelectedValues={(values) => setSearch({...search, hubIds: values})} />
            <MultiSelect label={"Client"} values={clients} selectedValues={search.clientIds} setSelectedValues={(values) => setSearch({...search, clientIds: values})} />
          </>
        )}
        <DatePicker sx={{ mt: 2 }} label="Start" value={search.startDate} onChange={(v) => isCompleteDate(v) && setSearch({ ...search, startDate: moment(v).startOf('day').add(1, 'minute') })} disabled={isDispatcher} />
        <DatePicker sx={{ mt: 2 }} label="End" value={search.endDate} onChange={(v) => isCompleteDate(v) && setSearch({ ...search, endDate: moment(v).endOf('day').subtract(1, 'minute') })} disabled={isDispatcher} />
        <MultiSelect label={"Status"} values={orderStatuses} selectedValues={search.orderStatusIds} setSelectedValues={(values) => setSearch({...search, orderStatusIds: values})} />
        <FormControl sx={{ mt: 2, minWidth: 120 }}>
          <InputLabel id="alert-label">Alert</InputLabel>
          <Select SelectDisplayProps={selectDisplayProps} id="alert" name="alert" labelId="alert-label" label="Alert" value={search.alert || ""} onChange={handleOnChange} sx={selectSx}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="late">Late</MenuItem>
            <MenuItem value="pickup90min">Pickup Within 90min</MenuItem>
            <MenuItem value="preliminary">Preliminary</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ mt: 2, minWidth: 180 }}>
          <InputLabel id="dispatch-view-label">Dispatch View</InputLabel>
          <Select SelectDisplayProps={selectDisplayProps} id="dispatchQueueConfigId" name="dispatchQueueConfigId" labelId="dispatch-view-label" label="Dispatch View" value={search.dispatchQueueConfigId === null ? "" : search.dispatchQueueConfigId} onChange={handleOnChange} sx={selectSx}>
            <MenuItem value="">None</MenuItem>
            <MenuItem value={-1}>All</MenuItem>
            {dispatchQueues.map((queue) => (
              <MenuItem key={queue.dispatchQueueConfigId} value={queue.dispatchQueueConfigId}>{queue.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!isDispatcher && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          {/* Row 2: Trip stage pipeline */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #e0e0e0", borderRadius: 1, px: 1.5, pt: 1.5, pb: 0.5, backgroundColor: "#fafafa" }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 500 }}>Stage:</Typography>
            <FormControl size="small" sx={{ minWidth: 95 }}>
              <InputLabel id="closed-label">Closed</InputLabel>
              <Select SelectDisplayProps={selectDisplayProps} id="isClosed" labelId="closed-label" label="Closed" value={stageValue(search.isClosed)} onChange={(e) => setSearch({ ...search, isClosed: e.target.value === "all" ? null : e.target.value === "true" })} sx={selectSx}>
                <MenuItem value="all">All{stageCounts.closed != null && stageCounts.notClosed != null ? ` (${(stageCounts.closed + stageCounts.notClosed).toLocaleString()})` : ""}</MenuItem>
                <MenuItem value="true">Closed{stageCounts.closed != null ? ` (${stageCounts.closed.toLocaleString()})` : ""}</MenuItem>
                <MenuItem value="false">Not Closed{stageCounts.notClosed != null ? ` (${stageCounts.notClosed.toLocaleString()})` : ""}</MenuItem>
              </Select>
            </FormControl>
            <Typography color="text.disabled">›</Typography>
            <FormControl size="small" sx={{ minWidth: 95 }}>
              <InputLabel id="verified-label">Verified</InputLabel>
              <Select SelectDisplayProps={selectDisplayProps} id="isVerified" labelId="verified-label" label="Verified" value={stageValue(search.isVerified)} onChange={(e) => setSearch({ ...search, isVerified: e.target.value === "all" ? null : e.target.value === "true" })} sx={selectSx}>
                <MenuItem value="all">All{stageCounts.verified != null && stageCounts.notVerified != null ? ` (${(stageCounts.verified + stageCounts.notVerified).toLocaleString()})` : ""}</MenuItem>
                <MenuItem value="true">Verified{stageCounts.verified != null ? ` (${stageCounts.verified.toLocaleString()})` : ""}</MenuItem>
                <MenuItem value="false">Not Verified{stageCounts.notVerified != null ? ` (${stageCounts.notVerified.toLocaleString()})` : ""}</MenuItem>
              </Select>
            </FormControl>
            <Typography color="text.disabled">›</Typography>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="approved-label">Approved</InputLabel>
              <Select SelectDisplayProps={selectDisplayProps} id="isApproved" labelId="approved-label" label="Approved" value={stageValue(search.isApproved)} onChange={(e) => setSearch({ ...search, isApproved: e.target.value === "all" ? null : e.target.value === "true" })} sx={selectSx}>
                <MenuItem value="all">All{stageCounts.approved != null && stageCounts.notApproved != null ? ` (${(stageCounts.approved + stageCounts.notApproved).toLocaleString()})` : ""}</MenuItem>
                <MenuItem value="true">Approved{stageCounts.approved != null ? ` (${stageCounts.approved.toLocaleString()})` : ""}</MenuItem>
                <MenuItem value="false">Not Approved{stageCounts.notApproved != null ? ` (${stageCounts.notApproved.toLocaleString()})` : ""}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Row 2 continued: Flags */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel id="mpv-label">MPV</InputLabel>
            <Select SelectDisplayProps={selectDisplayProps} id="isMpv" labelId="mpv-label" label="MPV" value={stageValue(search.isMpv)} onChange={(e) => setSearch({ ...search, isMpv: e.target.value === "all" ? null : e.target.value === "true" })} sx={selectSx}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">MPV</MenuItem>
              <MenuItem value="false">Not MPV</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox size="small" checked={!!search.isCancelled} onChange={() => setSearch({ ...search, isCancelled: !search.isCancelled })} />} label="Cancelled" />
          <FormControlLabel control={<Checkbox size="small" checked={!!search.isMapped} onChange={() => setSearch({ ...search, isMapped: search.isMapped ? null : true })} />} label="Mgr Review" />
          <FormControlLabel control={<Checkbox size="small" checked={!!search.isNearOtp} onChange={() => setSearch({ ...search, isNearOtp: search.isNearOtp ? null : true })} />} label="Near OTP" />

          <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
            {approveButton}
            <MDButton onClick={clearFilters}>Clear</MDButton>
          </Box>
        </Box>
      )}

      {isDispatcher && (
        <MDButton sx={{ mt: 1 }} onClick={clearFilters}>Clear</MDButton>
      )}
    </Box>
  );
};
export default ListFilter;
